import React, { useState, useEffect } from 'react';
import { DialogComponent } from '@syncfusion/ej2-react-popups';
import { GridComponent, ColumnsDirective, ColumnDirective, Inject, Sort, Filter } from '@syncfusion/ej2-react-grids';
import { useTranslation } from 'react-i18next';
import { useErrorHandler } from '../../utils/errorHandler';
import { useToast } from '../../hooks/useToast';
import type { ProductionScheduleDto, ProductionOrderDto, ResourceDto, OptimizationErrorDetail } from '../../api';
import { ReactTabs } from '../../components/ReactTabs';
import { ProductionOrderControllerService } from '../../api/services/ProductionOrderControllerService';
import { ResourceControllerService } from '../../api/services/ResourceControllerService';
import { MaterialControllerService } from '../../api/services/MaterialControllerService';

interface ScheduleInputDataDialogProps {
  schedule: ProductionScheduleDto;
  isOpen: boolean;
  onClose: () => void;
}

interface CampaignDisplayRow {
  sequenceNumber: number;
  campaignId: string;
  materialId: string;
  materialName?: string;
  desiredQuantity: number;
  totalBakedQuantity?: number;
  dueDate: string;
  doughBatchCount: number;
  ovenBatchCount: number;
}

interface ProductionOrderDisplayRow {
  orderNumber: string;
  materialId: string;
  materialName?: string;
  quantity: number;
  status: string;
  campaignId?: string;
}

interface ResourceDisplayRow {
  resourceName: string;
  resourceTypeName: string;
  status: string;
}

const ScheduleInputDataDialog: React.FC<ScheduleInputDataDialogProps> = ({
  schedule,
  isOpen,
  onClose
}) => {
  const { t, i18n } = useTranslation(['common']);
  const { handleError } = useErrorHandler();
  const { showToast } = useToast();
  const [loading, setLoading] = useState(false);
  const [campaigns, setCampaigns] = useState<CampaignDisplayRow[]>([]);
  const [orders, setOrders] = useState<ProductionOrderDisplayRow[]>([]);
  const [resources, setResources] = useState<ResourceDisplayRow[]>([]);
  const [errorInfo, setErrorInfo] = useState<any>(null);

  useEffect(() => {
    if (isOpen && schedule) {
      loadData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- loadData is a local function; including it would trigger on every render
  }, [isOpen, schedule]);

  const loadData = async () => {
    setLoading(true);
    try {
      // Load campaigns from schedule
      const campaignRows: CampaignDisplayRow[] = [];
      if (schedule.campaigns) {
        for (const campaign of schedule.campaigns) {
          let materialName = campaign.materialId || '';
          try {
            if (campaign.materialId) {
              const material = await MaterialControllerService.findByUuid(campaign.materialId);
              materialName = material.name || campaign.materialId;
            }
          } catch (e) {
            // Material not found, use ID
          }

          campaignRows.push({
            sequenceNumber: campaign.sequenceNumber || 0,
            campaignId: campaign.campaignId || '',
            materialId: campaign.materialId || '',
            materialName: materialName,
            desiredQuantity: campaign.desiredQuantity || 0,
            totalBakedQuantity: campaign.totalBakedQuantity,
            dueDate: campaign.dueDate || '',
            doughBatchCount: campaign.doughBatches?.length || 0,
            ovenBatchCount: campaign.ovenBatches?.length || 0
          });
        }
      }
      campaignRows.sort((a, b) => a.sequenceNumber - b.sequenceNumber);
      setCampaigns(campaignRows);

      // Load production orders
      const orderRows: ProductionOrderDisplayRow[] = [];
      if (schedule.productionOrderIds && schedule.productionOrderIds.length > 0) {
        for (const orderId of schedule.productionOrderIds) {
          try {
            const order = await ProductionOrderControllerService.findByUuid(orderId);
            let materialName = order.materialId || '';
            try {
              if (order.materialId) {
                const material = await MaterialControllerService.findByUuid(order.materialId);
                materialName = material.name || order.materialId;
              }
            } catch (e) {
              // Material not found
            }

            orderRows.push({
              orderNumber: order.orderNumber || orderId,
              materialId: order.materialId || '',
              materialName: materialName,
              quantity: order.outputQuantity ?? 0,
              status: order.status || '',
              campaignId: (order as ProductionOrderDto & { campaignId?: string }).campaignId
            });
          } catch (e) {
            // Order not found, add with minimal info
            orderRows.push({
              orderNumber: orderId,
              materialId: '',
              quantity: 0,
              status: 'unknown',
              campaignId: undefined
            });
          }
        }
      }
      setOrders(orderRows);

      // Load resources
      const resourceRows: ResourceDisplayRow[] = [];
      if (schedule.resourceIds && schedule.resourceIds.length > 0) {
        for (const resourceId of schedule.resourceIds) {
          try {
            const resource = await ResourceControllerService.findByUuid(resourceId);
            resourceRows.push({
              resourceName: resource.name || resourceId,
              resourceTypeName: resource.resourceTypeName || '',
              status: (resource as ResourceDto & { isActive?: boolean }).isActive !== false ? 'active' : 'inactive'
            });
          } catch (e) {
            // Resource not found, add with minimal info
            resourceRows.push({
              resourceName: resourceId,
              resourceTypeName: '',
              status: 'unknown'
            });
          }
        }
      }
      setResources(resourceRows);

      // Assemble the error info for the 'failed' status:
      // the backend mapper returns optimizationErrorCode + optimizationErrors as dedicated fields.
      // The error message itself lives under the key 'error' in optimizationParameters.
      if (schedule.status === 'failed') {
        const errorCode = schedule.optimizationErrorCode ?? null;
        const errors = schedule.optimizationErrors ?? null;
        let errorMessage: string | null = null;
        if (schedule.optimizationParameters) {
          try {
            const parsed = JSON.parse(schedule.optimizationParameters);
            errorMessage = parsed.error || parsed.message || null;
          } catch {
            errorMessage = schedule.optimizationParameters;
          }
        }
        if (errorCode || (errors && errors.length > 0) || errorMessage) {
          setErrorInfo({ error: errorMessage, errorCode, errors });
        }
      }
    } catch (error) {
      handleError(error, 'loading schedule input data');
      showToast('error', t('common.error'), 'Failed to load schedule input data');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      if (i18n.language === 'de') {
        const day = date.getDate().toString().padStart(2, '0');
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const year = date.getFullYear();
        return `${day}.${month}.${year}`;
      } else {
        return new Intl.DateTimeFormat('en-US', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit'
        }).format(date);
      }
    } catch {
      return dateString;
    }
  };

  const formatDateRange = () => {
    if (!schedule.plannedStart && !schedule.plannedEnd) return '-';
    const start = schedule.plannedStart ? formatDate(schedule.plannedStart) : '-';
    const end = schedule.plannedEnd ? formatDate(schedule.plannedEnd) : '-';
    return `${start} - ${end}`;
  };

  return (
    <DialogComponent
      visible={isOpen}
      isModal={true}
      header={t('productionSchedule.inputDataView.title', 'Eingangsdaten - {{scheduleName}}', { scheduleName: schedule.scheduleName || schedule.uuid })}
      showCloseIcon={true}
      closeOnEscape={true}
      width="90%"
      height="80%"
      close={onClose}
      animationSettings={{ effect: 'None' }}
    >
      <div className="p-4 h-full flex flex-col">
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-lg">{t('common.loading')}</div>
          </div>
        ) : (
          <ReactTabs
            style={{ flex: 1 }}
            items={[
              // Overview Tab
              {
                header: t('productionSchedule.inputDataView.tabs.overview', 'Übersicht'),
                content: () => (
                <div className="p-4 space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="font-semibold">{t('productionSchedule.inputDataView.overview.scheduleName', 'Plan-Name')}</label>
                      <p>{schedule.scheduleName || '-'}</p>
                    </div>
                    <div>
                      <label className="font-semibold">{t('productionSchedule.inputDataView.overview.timeRange', 'Zeitraum')}</label>
                      <p>{formatDateRange()}</p>
                    </div>
                    <div>
                      <label className="font-semibold">{t('productionSchedule.inputDataView.overview.campaignCount', 'Anzahl Kampagnen')}</label>
                      <p>{campaigns.length}</p>
                    </div>
                    <div>
                      <label className="font-semibold">{t('productionSchedule.inputDataView.overview.orderCount', 'Anzahl Aufträge')}</label>
                      <p>{orders.length}</p>
                    </div>
                    <div>
                      <label className="font-semibold">{t('productionSchedule.inputDataView.overview.resourceCount', 'Anzahl Ressourcen')}</label>
                      <p>{resources.length}</p>
                    </div>
                  </div>
                </div>
              ),
              },

              // Campaigns Tab
              {
                header: t('productionSchedule.inputDataView.tabs.campaigns', 'Kampagnen'),
                content: () => (
                <div className="p-4">
                  <GridComponent
                    locale="de-DE"
                    dataSource={campaigns}
                    allowPaging={true}
                    pageSettings={{ pageSize: 10 }}
                    allowSorting={true}
                    allowFiltering={true}
                    height="400"
                  >
                    <ColumnsDirective>
                      <ColumnDirective field="sequenceNumber" headerText={t('productionSchedule.inputDataView.campaigns.sequenceNumber', 'Sequenz')} width="80" />
                      <ColumnDirective field="campaignId" headerText={t('productionSchedule.inputDataView.campaigns.campaignId', 'Kampagne')} width="150" />
                      <ColumnDirective field="materialName" headerText={t('productionSchedule.inputDataView.campaigns.material', 'Material')} width="200" />
                      <ColumnDirective field="desiredQuantity" headerText={t('productionSchedule.inputDataView.campaigns.desiredQuantity', 'Gewünschte Menge')} width="120" type="number" />
                      <ColumnDirective field="totalBakedQuantity" headerText={t('productionSchedule.inputDataView.campaigns.totalBakedQuantity', 'Gebackene Menge')} width="120" type="number" />
                      <ColumnDirective field="dueDate" headerText={t('productionSchedule.inputDataView.campaigns.dueDate', 'Fälligkeitsdatum')} width="120" template={(props: any) => formatDate(props.dueDate)} />
                      <ColumnDirective field="doughBatchCount" headerText={t('productionSchedule.inputDataView.campaigns.doughBatchCount', 'Dough Batches')} width="120" type="number" />
                      <ColumnDirective field="ovenBatchCount" headerText={t('productionSchedule.inputDataView.campaigns.ovenBatchCount', 'Oven Batches')} width="120" type="number" />
                    </ColumnsDirective>
                    <Inject services={[Sort, Filter]} />
                  </GridComponent>
                </div>
              ),
              },

              // Orders Tab
              {
                header: t('productionSchedule.inputDataView.tabs.orders', 'Aufträge'),
                content: () => (
                <div className="p-4">
                  <GridComponent
                    locale="de-DE"
                    dataSource={orders}
                    allowPaging={true}
                    pageSettings={{ pageSize: 10 }}
                    allowSorting={true}
                    allowFiltering={true}
                    height="400"
                  >
                    <ColumnsDirective>
                      <ColumnDirective field="orderNumber" headerText={t('productionSchedule.inputDataView.orders.orderNumber', 'Auftragsnummer')} width="150" />
                      <ColumnDirective field="materialName" headerText={t('productionSchedule.inputDataView.orders.material', 'Material')} width="200" />
                      <ColumnDirective field="quantity" headerText={t('productionSchedule.inputDataView.orders.quantity', 'Menge')} width="100" type="number" />
                      <ColumnDirective field="status" headerText={t('productionSchedule.inputDataView.orders.status', 'Status')} width="120" />
                      <ColumnDirective field="campaignId" headerText={t('productionSchedule.inputDataView.orders.campaignId', 'Kampagne')} width="150" />
                    </ColumnsDirective>
                    <Inject services={[Sort, Filter]} />
                  </GridComponent>
                </div>
              ),
              },

              // Resources Tab
              {
                header: t('productionSchedule.inputDataView.tabs.resources', 'Ressourcen'),
                content: () => (
                <div className="p-4">
                  <GridComponent
                    locale="de-DE"
                    dataSource={resources}
                    allowPaging={true}
                    pageSettings={{ pageSize: 10 }}
                    allowSorting={true}
                    allowFiltering={true}
                    height="400"
                  >
                    <ColumnsDirective>
                      <ColumnDirective field="resourceName" headerText={t('productionSchedule.inputDataView.resources.resourceName', 'Ressource')} width="200" />
                      <ColumnDirective field="resourceTypeName" headerText={t('productionSchedule.inputDataView.resources.resourceType', 'Ressourcentyp')} width="200" />
                      <ColumnDirective field="status" headerText={t('productionSchedule.inputDataView.resources.status', 'Status')} width="120" />
                    </ColumnsDirective>
                    <Inject services={[Sort, Filter]} />
                  </GridComponent>
                </div>
              ),
              },

              // Errors/Warnings Tab (only for failed status)
              ...(schedule.status === 'failed' ? [{
                header: t('productionSchedule.inputDataView.tabs.errors', 'Fehler/Warnungen'),
                content: () => (
                  <div className="p-4">
                    {errorInfo && (
                      <div className="space-y-4">
                        {(errorInfo.error || errorInfo.message) && (
                          <div className="bg-red-50 border border-red-200 rounded p-4">
                            <h4 className="font-semibold text-red-800 mb-2">{t('productionSchedule.inputDataView.errors.errorMessage', 'Fehlermeldung')}</h4>
                            <p className="text-red-700">{errorInfo.error || errorInfo.message}</p>
                          </div>
                        )}
                        {errorInfo.errorCode && (
                          <div className="bg-yellow-50 border border-yellow-200 rounded p-4">
                            <h4 className="font-semibold text-yellow-800 mb-2">{t('productionSchedule.inputDataView.errors.errorCode', 'Fehlercode')}</h4>
                            <p className="text-yellow-700">{errorInfo.errorCode}</p>
                          </div>
                        )}
                        {Array.isArray(errorInfo.errors) && errorInfo.errors.length > 0 && (
                          <div className="bg-yellow-50 border border-yellow-200 rounded p-4">
                            <h4 className="font-semibold text-yellow-800 mb-2">{t('productionSchedule.inputDataView.errors.details', 'Fehlerdetails')}</h4>
                            <ul className="list-disc list-inside space-y-1 text-yellow-700 font-mono text-sm">
                              {errorInfo.errors.map((err: OptimizationErrorDetail, index: number) => (
                                <li key={index}>
                                  {err.errorCode && <span>[{err.errorCode}] </span>}
                                  {err.field && <span>{err.field}: </span>}
                                  {err.errorValues ? JSON.stringify(err.errorValues) : ''}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                        {schedule.routingValidationWarnings && schedule.routingValidationWarnings.length > 0 && (
                          <div className="bg-yellow-50 border border-yellow-200 rounded p-4">
                            <h4 className="font-semibold text-yellow-800 mb-2">{t('productionSchedule.inputDataView.errors.validationWarnings', 'Validierungswarnungen')}</h4>
                            <ul className="list-disc list-inside space-y-1 text-yellow-700">
                              {schedule.routingValidationWarnings.map((warning: any, index: number) => (
                                <li key={index}>{warning.message || JSON.stringify(warning)}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    )}
                    {!errorInfo && (
                      <p className="text-gray-500">{t('productionSchedule.inputDataView.errors.noErrorInfo', 'Keine Fehlerinformationen verfügbar')}</p>
                    )}
                  </div>
                ),
              }] : []),
            ]}
          />
        )}
      </div>
    </DialogComponent>
  );
};

export default ScheduleInputDataDialog;
